// 启动模拟前的确认浮层 / Confirm-before-start simulation modal.
// 两种形态共用一个容器：credit 充足 → 显示耗时 / Token / Credit 提示 + "再想想 / 确认启动"；
// credit 不足 → 显示当前余额和缺口 + "知道了"。返回一个 Promise，用户点"确认启动"才 resolve(true)。
// Two shapes share the same root: enough credit → timing + token + credit hints with
// "Cancel / Confirm" buttons; not enough credit → balance gap warning with a single
// acknowledge button. Resolves true only when the user clicks "确认启动".

import type { Mode } from "@/lib/types";

export interface ConfirmSimulationOptions {
  /** 本次模拟将扣除的 Credit 数 / Credits to be debited on start. */
  cost: number;
  /** 用户当前 Credit 余额 / Current user credit balance. */
  currentCredit: number;
  /** 仿真模式 / Simulation mode — 文案会用对应数量提示（single 1 条 / ab 2 条）. */
  mode: Mode;
}

/**
 * 打开启动确认弹窗 / Open the confirm-before-start modal.
 * 遵循 AGENTS.md 的约定：不响应外部点击关闭；Escape 也不关闭。只能通过浮层内按钮退出。
 * Follows AGENTS.md: no backdrop / Escape dismiss — user must click an in-modal button.
 */
export function showConfirmSimulationModal(opts: ConfirmSimulationOptions): Promise<boolean> {
  const root = document.getElementById("confirm-simulation-modal");
  if (!root) throw new Error("#confirm-simulation-modal mount missing");

  return new Promise<boolean>((resolve) => {
    const insufficient = opts.currentCredit < opts.cost;
    const gap = Math.max(0, opts.cost - opts.currentCredit);

    root.hidden = false;
    root.innerHTML = insufficient
      ? renderInsufficientHtml({ cost: opts.cost, current: opts.currentCredit, gap, mode: opts.mode })
      : renderConfirmHtml({ cost: opts.cost, current: opts.currentCredit, mode: opts.mode });

    const close = (result: boolean) => {
      root.hidden = true;
      root.innerHTML = "";
      resolve(result);
    };

    if (insufficient) {
      root.querySelector<HTMLButtonElement>(".confirm-sim-ack")
        ?.addEventListener("click", () => close(false));
      return;
    }

    root.querySelector<HTMLButtonElement>(".confirm-sim-cancel")
      ?.addEventListener("click", () => close(false));
    root.querySelector<HTMLButtonElement>(".confirm-sim-ok")
      ?.addEventListener("click", () => close(true));
  });
}

// 渲染"确认启动"表单 / Render the confirm-start form (enough credit path).
function renderConfirmHtml(ctx: { cost: number; current: number; mode: Mode }): string {
  const planNoun = ctx.mode === "ab" ? "two plans" : "one plan";
  return `
    <div class="overlay"></div>
    <div class="modal confirm-sim-modal">
      <div class="confirm-sim-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor"
          stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2 1.5 21h21z" />
          <line x1="12" y1="9" x2="12" y2="14" />
          <circle cx="12" cy="17.5" r="1.1" fill="currentColor" stroke="none" />
        </svg>
      </div>
      <h3>Start simulation?</h3>
      <ul class="confirm-sim-notes">
        <li>
          <span class="confirm-sim-bullet" aria-hidden="true">⏱</span>
          <span>The simulation can take <strong>30 minutes or longer</strong>. You can close this page and come back later to check progress.</span>
        </li>
      </ul>
      <div class="confirm-sim-credit">
        <div class="confirm-sim-credit-row">
          <span class="confirm-sim-credit-label">Cost</span>
          <span class="confirm-sim-credit-value">${ctx.cost} Credit (${escape(planNoun)})</span>
        </div>
        <div class="confirm-sim-credit-row">
          <span class="confirm-sim-credit-label">Current balance</span>
          <span class="confirm-sim-credit-value">${ctx.current} Credit</span>
        </div>
      </div>
      <div class="confirm-sim-actions">
        <button type="button" class="button ghost confirm-sim-cancel">Cancel</button>
        <button type="button" class="button primary confirm-sim-ok">Start Simulation</button>
      </div>
    </div>
  `;
}

// 渲染"余额不足"表单 / Render the insufficient-credit form.
function renderInsufficientHtml(ctx: {
  cost: number;
  current: number;
  gap: number;
  mode: Mode;
}): string {
  const planNoun = ctx.mode === "ab" ? "A/B PMF simulation" : "single-plan simulation";
  return `
    <div class="overlay"></div>
    <div class="modal confirm-sim-modal">
      <div class="confirm-sim-icon danger" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor"
          stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2 1.5 21h21z" />
          <line x1="12" y1="9" x2="12" y2="14" />
          <circle cx="12" cy="17.5" r="1.1" fill="currentColor" stroke="none" />
        </svg>
      </div>
      <h3>Insufficient Credit</h3>
      <p class="confirm-sim-desc">Starting this ${escape(planNoun)} requires <strong>${ctx.cost} Credit</strong>, which is more than your current balance.</p>
      <div class="confirm-sim-credit danger">
        <div class="confirm-sim-credit-row">
          <span class="confirm-sim-credit-label">Required</span>
          <span class="confirm-sim-credit-value">${ctx.cost} Credit</span>
        </div>
        <div class="confirm-sim-credit-row">
          <span class="confirm-sim-credit-label">Current balance</span>
          <span class="confirm-sim-credit-value">${ctx.current} Credit</span>
        </div>
        <div class="confirm-sim-credit-row highlight">
          <span class="confirm-sim-credit-label">Shortfall</span>
          <span class="confirm-sim-credit-value">${ctx.gap} Credit</span>
        </div>
      </div>
      <p class="confirm-sim-hint">Please ask an administrator to add more Credit, or reduce the number of plans in this simulation.</p>
      <div class="confirm-sim-actions single">
        <button type="button" class="button primary confirm-sim-ack">Got it</button>
      </div>
    </div>
  `;
}

// 纯字符串转义 / Plain HTML escape for interpolated strings.
function escape(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
